export const TabButton = ({
	label,
	disabled = false,
	leftIcon: LeftIcon,
	rightIcon: RightIcon,
	iconSize = 16,
	iconAriaHidden = true,
	onClick,
	onKeyDown,
	className,
	labelClassName,
	iconClassName,
	ref,
	...props
}) => {
	return (
		<button
			ref={ref}
			className={className}
			disabled={disabled}
			type='button'
			onClick={onClick}
			onKeyDown={onKeyDown}
			{...props}
		>
			{LeftIcon && (
				<LeftIcon className={iconClassName} size={iconSize} aria-hidden={iconAriaHidden} focusable={false} />
			)}
			{label && <span className={labelClassName}>{label}</span>}
			{RightIcon && (
				<RightIcon className={iconClassName} size={iconSize} aria-hidden={iconAriaHidden} focusable={false} />
			)}
		</button>
	);
};

/**
 * TabButton — reusable button with optional left/right icons.
 * All styling comes from the calling component via className props.
 *
 * --- Props ---
 *
 * label          {string}     Button text.
 * disabled       {boolean}    Disables the button. Default: false.
 *                             Hover is blocked automatically via :not(:disabled):hover in CSS.
 * leftIcon       {Component}  SVG icon component rendered before the label. Optional.
 * rightIcon      {Component}  SVG icon component rendered after the label. Optional.
 * iconSize       {number}     Icon size in px passed to the icon component. Default: 16.
 * onClick        {function}   Click handler.
 * onKeyDown      {function}   Keydown handler (used by Tabs for arrow key navigation).
 * className      {string}     Classes on <button> (layout, colors, states).
 * labelClassName {string}     Classes on <span> wrapping the label (typography).
 * iconClassName  {string}     Classes on both icon components (size, color).
 * ...props                    Any HTML / ARIA attribute passed directly to <button>:
 *                               id, role, tabIndex, aria-label, aria-selected,
 *                               aria-controls, aria-expanded, data-*, etc.
 *
 * --- Variants ---
 *
 * 1. Label only
 *    <TabButton
 *      label="Save"
 *      disabled={false}
 *      className="button-primary button-primary--icon-none"
 *      labelClassName="semantic-button-primary"
 *      onClick={handleClick}
 *    />
 *
 * 2. Left icon + label
 *    <TabButton
 *      label="Add"
 *      leftIcon={PlusIcon}
 *      disabled={false}
 *      className="button-primary button-primary--icon-leading"
 *      labelClassName="semantic-button-primary"
 *      iconClassName="button-primary__icon"
 *      onClick={handleClick}
 *    />
 *
 * 3. Label + right icon
 *    <TabButton
 *      label="Next"
 *      rightIcon={ChevronRightIcon}
 *      disabled={false}
 *      className="button-primary button-primary--icon-trailing"
 *      labelClassName="semantic-button-primary"
 *      iconClassName="button-primary__icon"
 *      onClick={handleClick}
 *    />
 *
 * 4. Icon only (no label)
 *    <TabButton
 *      leftIcon={PlusIcon}
 *      aria-label="Add item"
 *      className=""
 *      iconClassName=""
 *      onClick={handleClick}
 *    />
 *
 * 5. Disabled
 *    <TabButton
 *      label="Save"
 *      leftIcon={PlusIcon}
 *      disabled={true}
 *      className="button-primary button-primary--icon-leading"
 *      labelClassName="semantic-button-primary"
 *      iconClassName="button-primary__icon"
 *    />
 *
 * --- Usage as a tab (inside a tablist) ---
 *
 *    <TabButton
 *      label="Overview"
 *      disabled={false}
 *      className="tab"
 *      labelClassName="body-medium-base"
 *      id="tab-overview"
 *      role="tab"
 *      aria-selected={true}
 *      aria-controls="tabpanel-overview"
 *      tabIndex={0}
 *      onClick={handleChange}
 *      onKeyDown={handleKeyDown}
 *    />
 */
